import type { ActionWidgetType } from 'types/entity'
import type { Brief } from './buildBrief'
import { calendarDaysBetween, relativeDay } from './parseWhen'

/**
 * Suggested next steps - the rule-based kind. Free, instant, and the same every time for the same
 * record, because each one is a plain consequence of a fact already shown in the panel above it:
 * a message nobody answered, a date that has passed, a call that was not picked up, a record with
 * nothing written on it.
 *
 * These are deliberately not "AI". They never guess what the customer wants or what to say - that
 * is what the paid AI read is for. A suggestion here only ever says: given what is on the record,
 * this is the obvious thing to do now, and here is the button that does it.
 */

export interface Suggestion {
  id: string
  text: string
  /** The fact on the record this follows from - shown under the suggestion. */
  why: string
  /** The action-panel form that carries it out, when there is one. */
  action?: ActionWidgetType
  actionLabel?: string
}

/** Long enough that "we spoke recently" no longer holds. */
const QUIET_DAYS = 14
const MAX_SUGGESTIONS = 3

const shorten = (text: string, max = 80) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text)

export function suggestNextSteps(brief: Brief, now: Date): Suggestion[] {
  const out: Suggestion[] = []
  const { waitingOnUs, nextSteps, lastContact, lastWords, timeline, newToYou, people } = brief

  if (waitingOnUs?.when) {
    out.push({
      id: 'reply',
      text: 'Reply to their WhatsApp message',
      why: `They wrote ${relativeDay(waitingOnUs.when, now)}: "${shorten(waitingOnUs.body || '(no text)')}" — nothing has gone back since.`,
      action: 'whatsapp',
      actionLabel: 'Open WhatsApp',
    })
  }

  const overdueFollowUp = nextSteps.find((s) => s.kind === 'followup' && (s.overdueDays > 0 || (s.hasTime && s.when < now)))
  if (overdueFollowUp) {
    out.push({
      id: 'overdue-followup',
      text: 'Make the overdue call-back, then log it with the next date',
      why: `${overdueFollowUp.label || 'Follow-up'} was due ${relativeDay(overdueFollowUp.when, now)}${overdueFollowUp.who ? ` (${overdueFollowUp.who})` : ''}.`,
      action: 'followup',
      actionLabel: 'Log follow-up',
    })
  }

  // The newest thing we did towards the customer was a call they did not pick up.
  const newestOutreach = timeline.find((t) => t.when && t.channel !== 'note' && t.channel !== 'activity' && t.direction !== 'in')
  if (newestOutreach?.unanswered && newestOutreach.when && !waitingOnUs) {
    out.push({
      id: 'unanswered-call',
      text: 'The last call was not picked up — send a WhatsApp, or try at a different hour',
      why: `${newestOutreach.title} ${relativeDay(newestOutreach.when, now)}${newestOutreach.who ? ` by ${newestOutreach.who}` : ''}.`,
      action: 'whatsapp',
      actionLabel: 'Open WhatsApp',
    })
  }

  const overdueTask = nextSteps.find((s) => s.kind === 'task' && s.overdueDays > 0)
  if (overdueTask) {
    out.push({
      id: 'overdue-task',
      text: `Finish the open task: ${overdueTask.label}`,
      why: `Due ${relativeDay(overdueTask.when, now)}${overdueTask.who ? `, with ${overdueTask.who}` : ''}.`,
    })
  }

  const stuck = brief.stuckDeals[0]
  if (stuck) {
    out.push({
      id: 'stuck-deal',
      text: `Move the deal on or close it — "${stuck.stage}" has not changed for ${stuck.days} days`,
      why: `${stuck.pipeline}: reached ${stuck.stage} ${relativeDay(stuck.since, now)} and nothing since.`,
      action: 'followup',
      actionLabel: 'Log where it stands',
    })
  }

  if (nextSteps.length === 0 && timeline.length > 0) {
    out.push({
      id: 'no-next-step',
      text: 'Set the next follow-up date',
      why: 'Nothing is planned on this record — no follow-up date, open task or appointment.',
      action: 'followup',
      actionLabel: 'Log follow-up',
    })
  }

  if (!lastWords && lastContact) {
    out.push({
      id: 'nothing-written',
      text: 'Write down what they need after you speak',
      why: 'There has been contact, but no follow-up remark says what this customer wants. The next person will start from zero.',
      action: 'followup',
      actionLabel: 'Log follow-up',
    })
  }

  if (lastContact?.when && nextSteps.length > 0 && !overdueFollowUp) {
    const quiet = calendarDaysBetween(lastContact.when, now)
    if (quiet >= QUIET_DAYS) {
      out.push({
        id: 'gone-quiet',
        text: 'Check in — it has been a while',
        why: `Last contact was ${relativeDay(lastContact.when, now)}.`,
        action: 'whatsapp',
        actionLabel: 'Open WhatsApp',
      })
    }
  }

  if (newToYou) {
    const others = people.filter((p) => !p.isYou).map((p) => p.name)
    out.push({
      id: 'new-to-you',
      text: 'Read the timeline before you call — this customer is new to you',
      why: `So far handled by ${others.slice(0, 2).join(' and ')}${others.length > 2 ? ` and ${others.length - 2} more` : ''}.`,
    })
  }

  if (timeline.length === 0) {
    out.push({
      id: 'first-touch',
      text: 'Make the first contact and note what they are looking for',
      why: 'Nothing is recorded yet.',
      action: 'followup',
      actionLabel: 'Log follow-up',
    })
  }

  return out.slice(0, MAX_SUGGESTIONS)
}
